import { MediaPlayer } from './media_player';

import styles from '../player.module.css';

export class BaseVideoPlayer extends MediaPlayer {
  declare mediaElement: HTMLVideoElement;

  initMediaElement(): void {
    if (!this.mediaElement) {
      throw new Error('Media element is not available');
    }
    this.mediaElement.classList.add(styles.player, styles.transforming_player);
    this.mediaElement.playsInline = true;
    this.mediaElement.crossOrigin = 'anonymous';
    this.mediaElement.preload = 'auto';
    this.mediaElement.disablePictureInPicture = true;
    this.mediaElement.disableRemotePlayback = true;

    // https://wicg.github.io/video-rvfc/
    // requestVideoFrameCallback is called each time when a frame is presented,
    // so it works for both play and paused state, and it supposed to work
    // for any video frame rate without utilizing too much CPU.
    if (!this.isComparePlayer) {
      this.mediaElement.requestVideoFrameCallback(this.#handleVideoFrame);
    }

    return;
  }

  async requestFullscreen() {
    const mediaElement = this.mediaElement;
    if (!mediaElement) {
      return;
    }
    if (mediaElement.requestFullscreen) {
      return mediaElement.requestFullscreen();
    }
    if (mediaElement.webkitRequestFullscreen) {
      return mediaElement.webkitRequestFullscreen();
    }

    if (
      mediaElement.webkitSupportsFullscreen &&
      mediaElement.webkitEnterFullscreen
    ) {
      mediaElement.addEventListener(
        'webkitendfullscreen',
        () => {
          this.store.fullscreen.setFullscreen(false);
        },
        { once: true },
      );
      return mediaElement.webkitEnterFullscreen();
    }

    throw new Error('Fullscreen API is not supported');
  }

  #handleVideoFrame = (_now: number, metadata: VideoFrameCallbackMetadata) => {
    const playing = !this.mediaElement?.paused;

    // Experimentally determined number to an approximate lower level of the difference
    // of the metadata.mediaTime and actual frame displayed, to minimize how far into
    // "forbidden" territory we go without not playing the last frames, and minimize
    // the amount of frames we jump after pausing.
    const currentTimeOffset = playing ? 0.02 : 0;
    const currentTime = metadata.mediaTime + currentTimeOffset;
    this.refreshStoreCurrentTime(currentTime);
    this.mediaElement?.requestVideoFrameCallback(this.#handleVideoFrame);
  };

  play() {
    super.play();
  }

  async destroy() {
    if (this.mediaElement) {
      this.mediaElement.src = '';
      this.mediaElement.load();
    }
    await super.destroy();
  }
}
